
import { PromoCode, Product as PromoProduct } from '@/types/promoCode';
import { getFromLocalStorage } from '../storage';
import { supabase } from "@/integrations/supabase/client";
import { parseSelectedProducts, safelyParseJson } from './helpers';

export const PROMO_CODES_KEY = 'promo_codes';

/**
 * Fetches all promo codes from database with fallback to local storage
 */
export const getPromoCodesFromStorage = async (): Promise<PromoCode[]> => {
  try {
    // First try to get from Supabase with a timeout for better error handling
    const { data, error } = await Promise.race([
      supabase.from('promo_codes').select('*'),
      new Promise<{data: null, error: {message: string}}>((_, reject) => 
        setTimeout(() => reject({data: null, error: {message: 'Connection timeout'}}), 5000)
      )
    ]);
    
    if (error) throw error;
    
    if (data && data.length > 0) {
      // Map the Supabase data to our PromoCode type
      return data.map((code: any) => {
        // Convert selected_products from JSON to Product[]
        let selectedProducts: PromoProduct[] = [];
        if (code.selected_products) {
          try {
            // Ensure selected_products is an array
            const parsedProducts = Array.isArray(code.selected_products) 
              ? code.selected_products 
              : typeof code.selected_products === 'string' 
                ? safelyParseJson(code.selected_products) 
                : code.selected_products;
            
            // Map to ensure all required Product fields are present
            selectedProducts = Array.isArray(parsedProducts) ? parsedProducts.map((p: any) => ({
              id: p.id || 0,
              name: p.name || '',
              price: p.price || 0,
              description: p.description || '',
              image: p.image || '',
              category: p.category || ''
            })) : [];
          } catch (e) {
            console.error('Error parsing selected products:', e);
          }
        }
        
        // Type assertion for id and single_use
        const codeWithTypes = code as any;
        
        return {
          id: codeWithTypes.id || 0,
          code: code.code || '',
          discountPercentage: code.discount_percentage || 0,
          expiryDate: code.expiry_date ? new Date(code.expiry_date) : undefined,
          selectedProducts: selectedProducts,
          active: code.active,
          singleUse: Boolean(codeWithTypes.single_use)
        };
      });
    }
    
    // If no data in Supabase, fallback to localStorage
    const codes = getFromLocalStorage(PROMO_CODES_KEY);
    if (!codes) return [];
    
    // Convert date strings back to Date objects and ensure selectedProducts is the right type
    return codes.map((code: any) => ({
      ...code,
      expiryDate: code.expiryDate ? new Date(code.expiryDate) : undefined,
      selectedProducts: Array.isArray(code.selectedProducts) ? code.selectedProducts.map((p: any) => ({
        id: p.id || 0,
        name: p.name || '',
        price: p.price || 0,
        description: p.description || '',
        image: p.image || '',
        category: p.category || ''
      })) : []
    }));
  } catch (error) {
    console.error('Error fetching promo codes:', error);
    
    // Fallback to localStorage on error
    const codes = getFromLocalStorage(PROMO_CODES_KEY);
    if (!codes) return [];
    
    return codes.map((code: any) => ({
      ...code,
      expiryDate: code.expiryDate ? new Date(code.expiryDate) : undefined,
      selectedProducts: Array.isArray(code.selectedProducts) ? code.selectedProducts.map((p: any) => ({
        id: p.id || 0,
        name: p.name || '',
        price: p.price || 0,
        description: p.description || '',
        image: p.image || '',
        category: p.category || ''
      })) : []
    }));
  }
};
